import { useState, useMemo, useRef, useCallback, type FC } from "react";

// ─────────────────────────────────────────────────────────────────────────
// REAL IMPORTS — adjust the relative path below to match where this file
// lives relative to src/components/buttons/
// ─────────────────────────────────────────────────────────────────────────
import EyeButton from "@/app/components/common/actions/buttons/EyeButton";
import GxApplyButton from "@/app/components/common/actions/buttons/GxApplyButton";
import GxApplyButtonMd from "@/app/components/common/actions/buttons/GxApplyButtonMd";
import GxApplyButtonPayee from "@/app/components/common/actions/buttons/GxApplyButtonPayee";
import GxApplyButtonSmall from "@/app/components/common/actions/buttons/GxApplyButtonSmall";
import GxBlackButtonLink from "@/app/components/common/actions/buttons/GxBlackButtonLink";
import GxBlackButtonSmall from "@/app/components/common/actions/buttons/GxBlackButtonSmall";
import GxBlackDownloadButton from "@/app/components/common/actions/buttons/GxBlackDownloadButton";
import GxButton from "@/app/components/common/actions/buttons/GxButton";
import GxButtonSm from "@/app/components/common/actions/buttons/GxButtonSm";
import GxButtonWhiteXL from "@/app/components/common/actions/buttons/GxButtonWhiteXL";
import GxCancelButton from "@/app/components/common/actions/buttons/GxCancelButton";
import GxCancelButtonPayee from "@/app/components/common/actions/buttons/GxCancelButtonPayee";
import GxcommonBtn from "@/app/components/common/actions/buttons/GxcommonBtn";
import GxDeleteRedButton from "@/app/components/common/actions/buttons/GxDeleteRedButton";
import GxFilterButton from "@/app/components/common/actions/buttons/GxFilterButton";
import GxHoldButton from "@/app/components/common/actions/buttons/GxHoldButton";
import GxHorizontalLine from "@/app/components/common/actions/buttons/GxHorizontalLine";
import GxMultiSwitchButton from "@/app/components/common/actions/buttons/GxMultiSwitchButton";
import GxPinButton from "@/app/components/common/actions/buttons/GxPinButton";
import GxPrimaryButton from "@/app/components/common/actions/buttons/GxPrimaryButton";
import GxPrimaryButtonShadow from "@/app/components/common/actions/buttons/GxPrimaryButtonShadow";
import GxPrimarySmallButton from "@/app/components/common/actions/buttons/GxPrimarySmallButton";
import GxRedRejectButton from "@/app/components/common/actions/buttons/GxRedRejectButton";
import GxRedRejectButtonSmall from "@/app/components/common/actions/buttons/GxRedRejectButtonSmall";
import GxResendButton from "@/app/components/common/actions/buttons/GxResendButton";
import GxSaveChanges from "@/app/components/common/actions/buttons/GxSaveChanges";
import GxSecondaryButton from "@/app/components/common/actions/buttons/GxSecondaryButton";
import GxSecondaryButtonBlue from "@/app/components/common/actions/buttons/GxSecondaryButtonBlue";
import GxSecondaryButtonRed from "@/app/components/common/actions/buttons/GxSecondaryButtonRed";
import GxSecondaryButtonyellow from "@/app/components/common/actions/buttons/GxSecondaryButtonyellow";
import GxSecondarySmallButton from "@/app/components/common/actions/buttons/GxSecondarySmallButton";
// import GxSecondarySwitchButton from "@/app/components/common/actions/buttons/GxSecondarySwitchButton";
import GxSuspendButton from "@/app/components/common/actions/buttons/GxSuspendButton";
import GxTertiaryButton from "@/app/components/common/actions/buttons/GxTertiaryButton";
import GxUploadFileCancelBtn from "@/app/components/common/actions/buttons/GxUploadFileCancelBtn";
import GxUploadFileUploadBtn from "@/app/components/common/actions/buttons/GxUploadFileUploadBtn";
import GxViewInvoiceButton from "@/app/components/common/actions/buttons/GxViewInvoiceButton";
import GxViewManageButton from "@/app/components/common/actions/buttons/GxViewManageButton";

// ─────────────────────────────────────────────────────────────────────────
// PROP SHAPES — I've only seen two real source files:
//   - "children"  → GxButton.tsx    (children, variant, width, height, bgColor…)
//   - "label"      → GxcommonBtn.tsx (Kendo wrapper, label + onClick + className)
// Everything else below is a NAME-BASED GUESS, not verified against source.
// Anything guessed is marked ⚠️ — check that file before relying on this row.
// ─────────────────────────────────────────────────────────────────────────
type PropShape = "children" | "label" | "iconOnly" | "divider";

interface ButtonEntry {
  name: string;
  Component: FC<any>;
  shape: PropShape;
  verified: boolean; // true only for GxButton / GxcommonBtn themselves
  category: "primary" | "secondary" | "black" | "destructive" | "action" | "utility";
  sampleLabel: string;
}

function entry(
  name: string,
  Component: FC<any>,
  shape: PropShape,
  category: ButtonEntry["category"],
  sampleLabel: string,
  verified = false,
): ButtonEntry {
  return { name, Component, shape, verified, category, sampleLabel };
}

const ENTRIES: ButtonEntry[] = [
  // ── verified against real source ──
  entry("GxButton", GxButton, "children", "primary", "Button", true),
  entry("GxcommonBtn", GxcommonBtn, "label", "secondary", "Action", true),

  // ── guessed "children" shape (look/feel closest to GxButton) ──
  entry("GxPrimaryButton", GxPrimaryButton, "children", "primary", "Continue"),
  entry("GxPrimaryButtonShadow", GxPrimaryButtonShadow, "children", "primary", "Submit"),
  entry("GxPrimarySmallButton", GxPrimarySmallButton, "children", "primary", "Next"),
  entry("GxButtonSm", GxButtonSm, "children", "primary", "OK"),
  entry("GxButtonWhiteXL", GxButtonWhiteXL, "children", "primary", "Continue"),
  entry("GxSecondaryButton", GxSecondaryButton, "children", "secondary", "Cancel"),
  entry("GxSecondaryButtonBlue", GxSecondaryButtonBlue, "children", "secondary", "View"),
  entry("GxSecondaryButtonRed", GxSecondaryButtonRed, "children", "secondary", "Remove"),
  entry("GxSecondaryButtonyellow", GxSecondaryButtonyellow, "children", "secondary", "Pending"),
  entry("GxSecondarySmallButton", GxSecondarySmallButton, "children", "secondary", "Back"),
  entry("GxTertiaryButton", GxTertiaryButton, "children", "secondary", "Skip"),
  entry("GxBlackButtonLink", GxBlackButtonLink, "children", "black", "Learn more"),
  entry("GxBlackButtonSmall", GxBlackButtonSmall, "children", "black", "Details"),
  entry("GxCancelButton", GxCancelButton, "children", "destructive", "Cancel"),
  entry("GxCancelButtonPayee", GxCancelButtonPayee, "children", "destructive", "Cancel"),
  entry("GxDeleteRedButton", GxDeleteRedButton, "children", "destructive", "Delete"),
  entry("GxRedRejectButton", GxRedRejectButton, "children", "destructive", "Reject"),
  entry("GxRedRejectButtonSmall", GxRedRejectButtonSmall, "children", "destructive", "Reject"),
  entry("GxSuspendButton", GxSuspendButton, "children", "destructive", "Suspend"),
  entry("GxHoldButton", GxHoldButton, "children", "destructive", "Hold"),

  // ── guessed "label" shape (Kendo-style wrapper, likely i18n label like GxcommonBtn) ──
  entry("GxApplyButton", GxApplyButton, "label", "primary", "apply"),
  entry("GxApplyButtonMd", GxApplyButtonMd, "label", "primary", "apply"),
  entry("GxApplyButtonPayee", GxApplyButtonPayee, "label", "primary", "apply"),
  entry("GxApplyButtonSmall", GxApplyButtonSmall, "label", "primary", "apply"),
  entry("GxSaveChanges", GxSaveChanges, "label", "primary", "save_changes"),
  entry("GxBlackDownloadButton", GxBlackDownloadButton, "label", "black", "download"),
  // entry("GxSecondarySwitchButton", GxSecondarySwitchButton, "label", "secondary", "switch"),
  entry("GxResendButton", GxResendButton, "label", "action", "resend"),
  entry("GxUploadFileCancelBtn", GxUploadFileCancelBtn, "label", "action", "cancel_upload"),
  entry("GxUploadFileUploadBtn", GxUploadFileUploadBtn, "label", "action", "upload"),
  entry("GxViewInvoiceButton", GxViewInvoiceButton, "label", "action", "view_invoice"),
  entry("GxViewManageButton", GxViewManageButton, "label", "action", "manage"),
  entry("GxFilterButton", GxFilterButton, "label", "action", "filter"),

  // ── guessed icon/toggle shape — likely takes state + onClick, no label ──
  entry("EyeButton", EyeButton, "iconOnly", "utility", ""),
  entry("GxPinButton", GxPinButton, "iconOnly", "action", ""),
  entry("GxMultiSwitchButton", GxMultiSwitchButton, "iconOnly", "action", ""),

  // ── not a button at all ──
  entry("GxHorizontalLine", GxHorizontalLine, "divider", "utility", ""),
];

const CATEGORY_META: Record<ButtonEntry["category"], { label: string; icon: string }> = {
  primary: { label: "Primary", icon: "◈" },
  secondary: { label: "Secondary", icon: "◉" },
  black: { label: "Black", icon: "▣" },
  destructive: { label: "Destructive", icon: "⛔" },
  action: { label: "Action", icon: "⚡" },
  utility: { label: "Utility", icon: "◆" },
};

const CATEGORY_ORDER: ButtonEntry["category"][] = [
  "primary",
  "secondary",
  "black",
  "destructive",
  "action",
  "utility",
];

// Global type/variant options — matches the "outlined" | "filled" enum seen
// on GxButton.tsx. Applied to every rendered button via a spread prop; any
// component that doesn't read a `variant` prop just ignores it harmlessly.
const BUTTON_TYPES = ["outlined", "filled"] as const;
type ButtonType = (typeof BUTTON_TYPES)[number];

// ── Render each real component per its guessed shape ───────────────────────
function RenderedButton({ item, type }: { item: ButtonEntry; type: ButtonType }) {
  const noop = () => {};
  switch (item.shape) {
    case "children":
      return (
        <item.Component onClick={noop} variant={type}>
          {item.sampleLabel}
        </item.Component>
      );
    case "label":
      return <item.Component onClick={noop} label={item.sampleLabel} variant={type} />;
    case "iconOnly":
      return <item.Component onClick={noop} variant={type} />;
    case "divider":
      return <item.Component />;
  }
}

function buildSnippet(item: ButtonEntry, type: ButtonType): string {
  const importLine = `import ${item.name} from "@/components/buttons/${item.name}";`;
  let usageLine: string;
  switch (item.shape) {
    case "children":
      usageLine = `<${item.name} onClick={handleClick} variant="${type}">${item.sampleLabel}</${item.name}>`;
      break;
    case "label":
      usageLine = `<${item.name} onClick={handleClick} label="${item.sampleLabel}" variant="${type}" />`;
      break;
    case "iconOnly":
      usageLine = `<${item.name} onClick={handleClick} variant="${type}" />`;
      break;
    case "divider":
      usageLine = `<${item.name} />`;
      break;
  }
  const warning = item.verified ? "" : "\n// ⚠️ guessed props — verify against source";
  return `${importLine}\n${usageLine}${warning}`;
}

// ── Component ─────────────────────────────────────────────────────────────
const ButtonLibrary: FC = () => {
  const [activeCategory, setActiveCategory] = useState<ButtonEntry["category"]>("primary");
  const [search, setSearch] = useState("");
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [toastVisible, setToastVisible] = useState(false);
  const [globalType, setGlobalType] = useState<ButtonType>("outlined");
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isSearching = search.trim().length > 0;

  const filteredItems = useMemo<ButtonEntry[]>(() => {
    const q = search.trim().toLowerCase();
    const pool = isSearching ? ENTRIES : ENTRIES.filter((i) => i.category === activeCategory);
    if (!q) return pool;
    return pool.filter((i) => i.name.toLowerCase().includes(q));
  }, [activeCategory, search, isSearching]);

  const handleCopy = useCallback(async (item: ButtonEntry) => {
    try {
      await navigator.clipboard.writeText(buildSnippet(item, globalType));
    } catch {
      /* silent */
    }
    setCopiedKey(item.name);
    setToastVisible(true);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => {
      setCopiedKey(null);
      setToastVisible(false);
    }, 1800);
  }, [globalType]);

  const handleTabChange = (key: ButtonEntry["category"]) => {
    setActiveCategory(key);
    setSearch("");
  };

  return (
    <div className="min-h-screen bg-gray-50 w-full">
      <header className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-screen-xl mx-auto px-6">
          <div className="flex items-center justify-between gap-4 py-4 flex-wrap">
            <div>
              <h1 className="text-lg font-semibold text-gray-900 tracking-tight leading-tight">
                Button Library
              </h1>
              <p className="text-xs text-gray-400 mt-0.5 leading-none">
                Live-rendered real components — rows without ✓ are guessed prop shapes, verify before shipping
              </p>
            </div>

            <div className="flex items-center gap-3 flex-shrink-0">
              <label className="flex items-center gap-2 text-xs text-gray-500 font-medium">
                Type
                <select
                  value={globalType}
                  onChange={(e) => setGlobalType(e.target.value as ButtonType)}
                  className="text-sm bg-gray-100 border border-transparent rounded-lg outline-none focus:border-gray-300 focus:bg-white transition-all px-2.5 py-2 text-gray-800 capitalize"
                >
                  {BUTTON_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </label>

              <div className="relative">
                <input
                  type="text"
                  placeholder="Search buttons…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-3 pr-8 py-2 text-sm bg-gray-100 border border-transparent rounded-lg outline-none focus:border-gray-300 focus:bg-white transition-all w-52 text-gray-800 placeholder-gray-400"
                />
                {search && (
                  <button
                    onClick={() => setSearch("")}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center rounded-full bg-gray-300 text-gray-600 hover:bg-gray-400 text-xs leading-none"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          </div>

          <nav className="flex overflow-x-auto -mb-px">
            {CATEGORY_ORDER.map((key) => {
              const meta = CATEGORY_META[key];
              const count = ENTRIES.filter((i) => i.category === key).length;
              const isActive = !isSearching && activeCategory === key;
              return (
                <button
                  key={key}
                  onClick={() => handleTabChange(key)}
                  className={`group flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-all outline-none ${
                    isActive
                      ? "border-gray-800 text-gray-900"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <span className={`text-xs font-mono ${isActive ? "text-gray-700" : "text-gray-400"}`}>
                    {meta.icon}
                  </span>
                  {meta.label}
                  <span
                    className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-md ${
                      isActive ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </nav>
        </div>
      </header>

      <main className="max-w-screen-xl mx-auto px-6 py-5">
        <p className="text-[11px] text-gray-400 mb-4">
          <span className="text-gray-600 font-medium">{filteredItems.length}</span> buttons
          {isSearching && (
            <>
              {" "}
              &mdash; <span className="text-gray-700">"{search}"</span>
            </>
          )}
        </p>

        <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))" }}>
          {filteredItems.map((item) => {
            const isCopied = copiedKey === item.name;
            return (
              <button
                key={item.name}
                onClick={() => handleCopy(item)}
                title={buildSnippet(item, globalType)}
                className={`group relative flex flex-col items-center gap-2.5 rounded-xl p-3.5 border transition-all duration-150 outline-none bg-white ${
                  isCopied ? "border-green-300 bg-green-50" : "border-gray-200 hover:border-gray-300 hover:shadow-sm"
                }`}
              >
                <span
                  className={`absolute top-1.5 left-1.5 text-[8px] font-bold px-1 py-0.5 rounded ${
                    item.verified ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
                  }`}
                >
                  {item.verified ? "✓ verified" : "⚠ guessed"}
                </span>

                {isCopied && (
                  <span className="absolute top-1.5 right-1.5 w-4 h-4 flex items-center justify-center rounded-full bg-green-500 text-white text-[9px] font-bold leading-none">
                    ✓
                  </span>
                )}

                <div className="h-9 flex items-center justify-center mt-3 pointer-events-none">
                  <RenderedButton item={item} type={globalType} />
                </div>

                <span className="text-[10px] text-gray-500 text-center leading-tight break-all w-full font-mono">
                  {item.name}
                </span>
              </button>
            );
          })}
        </div>
      </main>

      <div
        className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 pointer-events-none flex items-center gap-2 bg-gray-900 text-white text-xs font-medium px-4 py-2.5 rounded-full shadow-xl transition-all duration-200 ${
          toastVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"
        }`}
      >
        Snippet copied to clipboard
      </div>
    </div>
  );
};

export default ButtonLibrary;